import { Card, Stack, Typography } from "@mui/material";
import { Fragment, useEffect, useRef } from "react";
import { PrimaryEditLink, StyledButton } from "react-common-ui-library";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ACCOUNT_CREATION_ROUTE,
  CONTAINED,
  CREATE_ACCOUNT_BUTTON,
  H2,
  H4,
  IS_RECAPTCHA_ENABLED,
  PROVIDER_ACCOUNT_CREATION,
  PROVIDER_INFO,
  VERIFY_ACCOUNT_ROUTE,
} from "../../../Constants/CommonConstants";
import { PROVIDER_SITE_INFO_USER_INFO_HEADING } from "../../../Constants/ProviderAndCertifierAccountCreationConstants";
import { REVIEW_YOUR_INFORMATION_EDIT } from "../../../Constants/ReviewYourInformationConstants";
import {
  SLICE_PROVIDER_ACCOUNT_CREATION,
  THUNK_POST_PROVIDER_OTP,
} from "../../../Constants/SliceAndThunkConstants";
import { OTP_SENT_SUCCESSFULLY } from "../../../Constants/VerifyAccountConstants";
import {
  clearProviderAccountCreationApiResponse,
  postProviderOTP,
  resetProviderAccountSendOTPDescription,
} from "../../../Features/ProviderAccountCreationSlice";
import { manageProviderAccountCreationSelector } from "../../../Selectors/ProviderAccountCreationSelector";
import {
  StyledProviderReviewInfoCardContent,
  StyledProviderReviewInfoCardStack,
  StyledProviderReviewInfoHeadingBox,
} from "../../../Styles/AccountCreation/ProviderAndCertifierAccountCreation/ProviderReviewInfoStyles";
import GoogleReCaptcha from "../../Application/GoogleReCaptcha";
import ProviderReviewInfoFields from "./ProviderReviewInfoFields";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import ConfigService from "../../../Services/ConfigService";
import ProviderReviewInfoHeaderAndSubtext from "./ProviderReviewInfoHeaderAndSubtext";

const isReCaptchaEnabled = ConfigService(IS_RECAPTCHA_ENABLED);

/**
 * @description Provider Review Info component that renders all the static data and makes an API call to create to account. Static data for each field is rendered by the
 * ProviderReviewInfoFields component.
 * @returns {ReactNode} React component consisting of heading and ProviderReviewInfoFields.
 */
const ProviderReviewInfo = () => {
  const reviewInformationRef = useRef();
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const intl = useIntl();

  const {
    dob,
    email,
    username,
    password,
    firstName,
    lastName,
    middleName,
    emailLanguage,
  } = useSelector((state) => manageProviderAccountCreationSelector(state));

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_PROVIDER_ACCOUNT_CREATION,
      THUNK_POST_PROVIDER_OTP
    )
  );

  const { isSuccess, description } = apiResponses;

  useEffect(() => {
    if (isSuccess && description === OTP_SENT_SUCCESSFULLY) {
      navigate(`/${ACCOUNT_CREATION_ROUTE}/${VERIFY_ACCOUNT_ROUTE}`, {
        state: { previousScreen: PROVIDER_ACCOUNT_CREATION },
      });
    }
    dispatch(clearProviderAccountCreationApiResponse(THUNK_POST_PROVIDER_OTP));
  }, [isSuccess, navigate, dispatch, description]);

  const onCreateAccountClickHandler = async () => {
    const token = isReCaptchaEnabled
      ? await reviewInformationRef.current.onSubmitWithReCAPTCHA()
      : null;
    if (token || !isReCaptchaEnabled) {
      const data = {
        dob: dob,
        email: email,
        userName: username,
        password: password,
        firstName: firstName,
        middleName: middleName,
        lastName: lastName,
        reCaptchaTkn: token,
        prefEmailLanguage: emailLanguage.description,
      };
      dispatch(postProviderOTP(data));
      dispatch(resetProviderAccountSendOTPDescription());
    }
  };

  const onEditLinkClickHandler = () => {
    navigate(`/${ACCOUNT_CREATION_ROUTE}/${PROVIDER_INFO}`);
  };

  return (
    <Fragment>
      <StyledProviderReviewInfoCardStack spacing={5}>
        <ProviderReviewInfoHeaderAndSubtext />
        <Stack spacing={8}>
          <Card elevation={0}>
            <StyledProviderReviewInfoCardContent>
              <Stack spacing={4}>
                <StyledProviderReviewInfoHeadingBox>
                  <Typography
                    component={H2}
                    variant={H4}
                    id={PROVIDER_SITE_INFO_USER_INFO_HEADING}
                  >
                    {intl.formatMessage({
                      id: PROVIDER_SITE_INFO_USER_INFO_HEADING,
                    })}
                  </Typography>
                  <PrimaryEditLink
                    label={intl.formatMessage({
                      id: REVIEW_YOUR_INFORMATION_EDIT,
                    })}
                    id={REVIEW_YOUR_INFORMATION_EDIT}
                    onClick={onEditLinkClickHandler}
                  />
                </StyledProviderReviewInfoHeadingBox>
                <ProviderReviewInfoFields />
              </Stack>
            </StyledProviderReviewInfoCardContent>
          </Card>
          <StyledButton
            variant={CONTAINED}
            onClick={onCreateAccountClickHandler}
            id={CREATE_ACCOUNT_BUTTON}
          >
            {intl.formatMessage({
              id: CREATE_ACCOUNT_BUTTON,
            })}
          </StyledButton>
        </Stack>
      </StyledProviderReviewInfoCardStack>
      {isReCaptchaEnabled ? (
        <GoogleReCaptcha ref={reviewInformationRef} />
      ) : null}
    </Fragment>
  );
};

export default ProviderReviewInfo;
